import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  Upload,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  Heart,
  Globe,
  Users,
  CreditCard,
  Award,
  Loader2
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section components for each step
const WelcomeSection = ({ onNext }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in">
    <div className="w-20 h-20 bg-[#f2c94c]/20 rounded-full flex items-center justify-center mb-6">
      <Award className="w-10 h-10 text-[#f2c94c]" />
    </div>
    <h1 className="font-serif text-4xl sm:text-5xl text-[#132238] mb-4">
      JamBassadors 2026 Application
    </h1>
    <p className="font-sans text-lg text-[#4b5563] max-w-lg mb-2">
      A curated recognition platform celebrating Jamaican excellence across the diaspora.
    </p>
    <div className="bg-[#fff8e5] border border-[#f5df9d] rounded-xl p-4 max-w-md mt-4 mb-8">
      <p className="font-sans text-sm text-[#8a5b00]">
        <strong>Important:</strong> You have been invited to apply for JamBassadors 2026.
        This is a selective recognition initiative. Submission does not guarantee selection.
      </p>
    </div>
    <button
      onClick={onNext}
      className="px-8 py-4 bg-[#132238] text-[#f2c94c] font-sans font-bold text-lg rounded-full hover:bg-[#1a2d4a] transition-all duration-300 shadow-xl flex items-center gap-3"
      data-testid="begin-application-btn"
    >
      Begin Application <ArrowRight size={20} />
    </button>
  </div>
);

const BasicInfoSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <User className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Basic Information</h2>
        <p className="font-sans text-sm text-[#4b5563]">Tell us about yourself</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.full_name}
            onChange={(e) => onChange("full_name", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.full_name ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="Enter your full name"
            data-testid="input-full-name"
          />
        </div>
        {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Email Address <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="email"
            value={data.email}
            onChange={(e) => onChange("email", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="your@email.com"
            data-testid="input-email"
          />
        </div>
        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="(123) 456-7890"
            data-testid="input-phone"
          />
        </div>
        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          City & Country of Residence <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.city_country}
            onChange={(e) => onChange("city_country", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.city_country ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="New York, USA"
            data-testid="input-city-country"
          />
        </div>
        {errors.city_country && <p className="text-red-500 text-sm mt-1">{errors.city_country}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Are you of Jamaican heritage? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {["Yes", "No", "Prefer not to say"].map((option) => (
            <label key={option} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-[#0b3d91] transition-colors">
              <input
                type="radio"
                name="jamaican_heritage"
                value={option}
                checked={data.jamaican_heritage === option}
                onChange={(e) => onChange("jamaican_heritage", e.target.value)}
                className="w-5 h-5 text-[#0b3d91] focus:ring-[#0b3d91]"
                data-testid={`radio-heritage-${option.toLowerCase().replace(/\s+/g, '-')}`}
              />
              <span className="font-sans text-[#132238]">{option}</span>
            </label>
          ))}
        </div>
        {errors.jamaican_heritage && <p className="text-red-500 text-sm mt-1">{errors.jamaican_heritage}</p>}
      </div>
    </div>
  </div>
);

const CategorySection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Award className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Category & Profile</h2>
        <p className="font-sans text-sm text-[#4b5563]">Your professional background</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Primary Area of Impact <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Business / Entrepreneurship",
            "Music / Entertainment",
            "Arts / Culture",
            "Education",
            "Government / Public Service",
            "Community Leadership"
          ].map((area) => (
            <label key={area} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${data.primary_area === area ? 'border-[#0b3d91] bg-[#0b3d91]/5' : 'border-gray-200 hover:border-[#0b3d91]'}`}>
              <input
                type="radio"
                name="primary_area"
                value={area}
                checked={data.primary_area === area}
                onChange={(e) => onChange("primary_area", e.target.value)}
                className="w-5 h-5 text-[#0b3d91] focus:ring-[#0b3d91]"
                data-testid={`radio-area-${area.toLowerCase().replace(/[\s\/]+/g, '-')}`}
              />
              <span className="font-sans text-sm text-[#132238]">{area}</span>
            </label>
          ))}
        </div>
        {errors.primary_area && <p className="text-red-500 text-sm mt-1">{errors.primary_area}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Current Role / Title <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.current_role}
            onChange={(e) => onChange("current_role", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.current_role ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="CEO, Artist, Educator, etc."
            data-testid="input-current-role"
          />
        </div>
        {errors.current_role && <p className="text-red-500 text-sm mt-1">{errors.current_role}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Organization / Brand (if applicable)
        </label>
        <input
          type="text"
          value={data.organization}
          onChange={(e) => onChange("organization", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="Company name or brand"
          data-testid="input-organization"
        />
      </div>
    </div>
  </div>
);

const ImpactSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <FileText className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Impact & Story</h2>
        <p className="font-sans text-sm text-[#4b5563]">This is the core scoring section</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Brief Bio (150–200 words) <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.brief_bio}
          onChange={(e) => onChange("brief_bio", e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border ${errors.brief_bio ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Tell us about yourself, your background, and your journey..."
          data-testid="textarea-brief-bio"
        />
        <p className="text-sm text-[#4b5563] mt-1">{data.brief_bio.split(/\s+/).filter(Boolean).length} words</p>
        {errors.brief_bio && <p className="text-red-500 text-sm mt-1">{errors.brief_bio}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          What impact have you made in your field or community? (200–300 words) <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.impact_made}
          onChange={(e) => onChange("impact_made", e.target.value)}
          rows={5}
          className={`w-full px-4 py-3 border ${errors.impact_made ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Describe the tangible impact you've made..."
          data-testid="textarea-impact-made"
        />
        <p className="text-sm text-[#4b5563] mt-1">{data.impact_made.split(/\s+/).filter(Boolean).length} words</p>
        {errors.impact_made && <p className="text-red-500 text-sm mt-1">{errors.impact_made}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          What makes your work meaningful or unique? (150–200 words) <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.unique_work}
          onChange={(e) => onChange("unique_work", e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border ${errors.unique_work ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="What sets you apart from others in your field..."
          data-testid="textarea-unique-work"
        />
        <p className="text-sm text-[#4b5563] mt-1">{data.unique_work.split(/\s+/).filter(Boolean).length} words</p>
        {errors.unique_work && <p className="text-red-500 text-sm mt-1">{errors.unique_work}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          List 2–3 measurable achievements (awards, milestones, reach, etc.) <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.achievements}
          onChange={(e) => onChange("achievements", e.target.value)}
          rows={3}
          className={`w-full px-4 py-3 border ${errors.achievements ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="• Achievement 1&#10;• Achievement 2&#10;• Achievement 3"
          data-testid="textarea-achievements"
        />
        {errors.achievements && <p className="text-red-500 text-sm mt-1">{errors.achievements}</p>}
      </div>
    </div>
  </div>
);

const ValuesSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Heart className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Alignment & Values</h2>
        <p className="font-sans text-sm text-[#4b5563]">Critical filter section</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          How does your work positively impact others? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.positive_impact}
          onChange={(e) => onChange("positive_impact", e.target.value)}
          rows={3}
          className={`w-full px-4 py-3 border ${errors.positive_impact ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Describe the positive impact of your work..."
          data-testid="textarea-positive-impact"
        />
        {errors.positive_impact && <p className="text-red-500 text-sm mt-1">{errors.positive_impact}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          How do you represent Jamaican culture, values, or legacy? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.jamaican_representation}
          onChange={(e) => onChange("jamaican_representation", e.target.value)}
          rows={3}
          className={`w-full px-4 py-3 border ${errors.jamaican_representation ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="How do you embody Jamaican heritage in your work..."
          data-testid="textarea-jamaican-representation"
        />
        {errors.jamaican_representation && <p className="text-red-500 text-sm mt-1">{errors.jamaican_representation}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Why do you believe you should be recognized as a JamBassador? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.why_recognized}
          onChange={(e) => onChange("why_recognized", e.target.value)}
          rows={3}
          className={`w-full px-4 py-3 border ${errors.why_recognized ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Why should you be selected..."
          data-testid="textarea-why-recognized"
        />
        {errors.why_recognized && <p className="text-red-500 text-sm mt-1">{errors.why_recognized}</p>}
      </div>
    </div>
  </div>
);

const DigitalSection = ({ data, onChange, headshot, onHeadshotChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Globe className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Digital Presence</h2>
        <p className="font-sans text-sm text-[#4b5563]">Your online profiles</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Website (optional)
        </label>
        <input
          type="url"
          value={data.website}
          onChange={(e) => onChange("website", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="https://yourwebsite.com"
          data-testid="input-website"
        />
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          LinkedIn
        </label>
        <input
          type="url"
          value={data.linkedin}
          onChange={(e) => onChange("linkedin", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="https://linkedin.com/in/yourprofile"
          data-testid="input-linkedin"
        />
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Instagram / Social Media
        </label>
        <input
          type="text"
          value={data.instagram}
          onChange={(e) => onChange("instagram", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="@yourhandle"
          data-testid="input-instagram"
        />
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Upload Headshot <span className="text-red-500">*</span>
        </label>
        <div className={`border-2 border-dashed ${errors.headshot ? 'border-red-500' : 'border-gray-300'} rounded-xl p-6 text-center hover:border-[#0b3d91] transition-colors`}>
          {headshot ? (
            <div className="flex flex-col items-center">
              <img src={headshot} alt="Headshot preview" className="w-32 h-32 object-cover rounded-full mb-3" />
              <button
                onClick={() => onHeadshotChange(null)}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Remove photo
              </button>
            </div>
          ) : (
            <label className="cursor-pointer">
              <Upload className="w-10 h-10 text-[#4b5563] mx-auto mb-2" />
              <p className="font-sans text-[#4b5563]">Click to upload your headshot</p>
              <p className="font-sans text-sm text-[#6b7280]">PNG, JPG up to 5MB</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      onHeadshotChange(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="hidden"
                data-testid="input-headshot"
              />
            </label>
          )}
        </div>
        {errors.headshot && <p className="text-red-500 text-sm mt-1">{errors.headshot}</p>}
      </div>
    </div>
  </div>
);

const ReferenceSection = ({ data, onChange }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Users className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">References</h2>
        <p className="font-sans text-sm text-[#4b5563]">Optional but powerful differentiator</p>
      </div>
    </div>

    <div className="bg-[#e9f8f0] border border-[#2bb673]/30 rounded-xl p-4 mb-6">
      <p className="font-sans text-sm text-[#16643d]">
        <strong>Tip:</strong> Adding a reference adds credibility and can be a big differentiator in the selection process.
      </p>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Reference Name
        </label>
        <input
          type="text"
          value={data.reference_name}
          onChange={(e) => onChange("reference_name", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="Full name of your reference"
          data-testid="input-reference-name"
        />
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Reference Contact
        </label>
        <input
          type="text"
          value={data.reference_contact}
          onChange={(e) => onChange("reference_contact", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="Email or phone number"
          data-testid="input-reference-contact"
        />
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Relationship
        </label>
        <input
          type="text"
          value={data.reference_relationship}
          onChange={(e) => onChange("reference_relationship", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="e.g., Former employer, Colleague, Mentor"
          data-testid="input-reference-relationship"
        />
      </div>
    </div>
  </div>
);

const PaymentSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <CreditCard className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Application Fee</h2>
        <p className="font-sans text-sm text-[#4b5563]">$75 USD processing fee</p>
      </div>
    </div>

    <div className="bg-[#fff8e5] border border-[#f5df9d] rounded-xl p-6 mb-6">
      <div className="flex items-center gap-4">
        <span className="font-serif text-4xl font-bold text-[#8a5b00]">$75</span>
        <div>
          <p className="font-sans font-semibold text-[#8a5b00]">Application Fee</p>
          <p className="font-sans text-sm text-[#7a5a1e]">One-time processing fee</p>
        </div>
      </div>
    </div>

    <div className="space-y-4">
      <label className={`flex items-start gap-3 p-4 border ${errors.fee_acknowledged ? 'border-red-500' : 'border-gray-200'} rounded-xl cursor-pointer hover:border-[#0b3d91] transition-colors`}>
        <input
          type="checkbox"
          checked={data.fee_acknowledged}
          onChange={(e) => onChange("fee_acknowledged", e.target.checked)}
          className="w-5 h-5 mt-0.5 text-[#0b3d91] focus:ring-[#0b3d91] rounded"
          data-testid="checkbox-fee-acknowledged"
        />
        <span className="font-sans text-[#132238]">
          I understand that a <strong>$75 application fee</strong> is required for processing.
        </span>
      </label>
      {errors.fee_acknowledged && <p className="text-red-500 text-sm">{errors.fee_acknowledged}</p>}
    </div>
  </div>
);

const TermsSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <CheckCircle className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Terms & Agreement</h2>
        <p className="font-sans text-sm text-[#4b5563]">Final confirmation</p>
      </div>
    </div>

    <div className="bg-gray-50 rounded-xl p-6 mb-6">
      <p className="font-sans text-[#132238] mb-4">By submitting this application, I understand that:</p>
      <ul className="space-y-2 font-sans text-[#4b5563]">
        <li className="flex items-start gap-2">
          <span className="text-[#0b3d91] mt-1">•</span>
          Submission does not guarantee selection
        </li>
        <li className="flex items-start gap-2">
          <span className="text-[#0b3d91] mt-1">•</span>
          Final decisions are made by the JamBassadors Selection Committee
        </li>
        <li className="flex items-start gap-2">
          <span className="text-[#0b3d91] mt-1">•</span>
          Selected honorees will be invited to participate at $950
        </li>
      </ul>
    </div>

    <label className={`flex items-start gap-3 p-4 border ${errors.agreed_to_terms ? 'border-red-500' : 'border-gray-200'} rounded-xl cursor-pointer hover:border-[#0b3d91] transition-colors`}>
      <input
        type="checkbox"
        checked={data.agreed_to_terms}
        onChange={(e) => onChange("agreed_to_terms", e.target.checked)}
        className="w-5 h-5 mt-0.5 text-[#0b3d91] focus:ring-[#0b3d91] rounded"
        data-testid="checkbox-agreed-terms"
      />
      <span className="font-sans text-[#132238]">
        I agree to the terms and conditions above
      </span>
    </label>
    {errors.agreed_to_terms && <p className="text-red-500 text-sm mt-2">{errors.agreed_to_terms}</p>}
  </div>
);

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Form data
  const [basicInfo, setBasicInfo] = useState({
    full_name: "",
    email: "",
    phone: "",
    city_country: "",
    jamaican_heritage: ""
  });
  
  const [category, setCategory] = useState({
    primary_area: "",
    current_role: "",
    organization: ""
  });
  
  const [impact, setImpact] = useState({
    brief_bio: "",
    impact_made: "",
    unique_work: "",
    achievements: ""
  });
  
  const [values, setValues] = useState({
    positive_impact: "",
    jamaican_representation: "",
    why_recognized: ""
  });
  
  const [digital, setDigital] = useState({
    website: "",
    linkedin: "",
    instagram: ""
  });
  
  const [reference, setReference] = useState({
    reference_name: "",
    reference_contact: "",
    reference_relationship: ""
  });
  
  const [headshot, setHeadshot] = useState(null);
  const [feeAcknowledged, setFeeAcknowledged] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const steps = [
    { name: "Welcome", component: "welcome" },
    { name: "Basic Info", component: "basic" },
    { name: "Category", component: "category" },
    { name: "Impact", component: "impact" },
    { name: "Values", component: "values" },
    { name: "Digital", component: "digital" },
    { name: "Reference", component: "reference" },
    { name: "Terms", component: "terms" }
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Basic Info
        if (!basicInfo.full_name.trim()) newErrors.full_name = "Full name is required";
        if (!basicInfo.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(basicInfo.email)) newErrors.email = "Invalid email format";
        if (!basicInfo.phone.trim()) newErrors.phone = "Phone number is required";
        if (!basicInfo.city_country.trim()) newErrors.city_country = "City & Country is required";
        if (!basicInfo.jamaican_heritage) newErrors.jamaican_heritage = "Please select an option";
        break;
      case 2: // Category
        if (!category.primary_area) newErrors.primary_area = "Please select your primary area";
        if (!category.current_role.trim()) newErrors.current_role = "Current role is required";
        break;
      case 3: // Impact
        if (!impact.brief_bio.trim()) newErrors.brief_bio = "Brief bio is required";
        if (!impact.impact_made.trim()) newErrors.impact_made = "Impact description is required";
        if (!impact.unique_work.trim()) newErrors.unique_work = "This field is required";
        if (!impact.achievements.trim()) newErrors.achievements = "Please list your achievements";
        break;
      case 4: // Values
        if (!values.positive_impact.trim()) newErrors.positive_impact = "This field is required";
        if (!values.jamaican_representation.trim()) newErrors.jamaican_representation = "This field is required";
        if (!values.why_recognized.trim()) newErrors.why_recognized = "This field is required";
        break;
      case 5: // Digital
        if (!headshot) newErrors.headshot = "Headshot is required";
        break;
      case 7: // Terms
        if (!agreedToTerms) newErrors.agreed_to_terms = "You must agree to the terms";
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep > 0 && !validateStep(currentStep)) {
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create application
      const applicationData = {
        basic_info: basicInfo,
        category: category,
        impact: impact,
        values: values,
        digital: digital,
        reference: reference.reference_name ? reference : null,
        headshot_data: headshot,
        agreed_to_terms: agreedToTerms,
        fee_acknowledged: true
      };
      
      const response = await axios.post(`${API}/applications`, applicationData);
      setSubmittedId(response.data.id);
      setIsSubmitted(true);
      
    } catch (error) {
      console.error("Submission error:", error);
      setErrors({ submit: error.response?.data?.detail || "Failed to submit application. Please try again." });
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (steps[currentStep].component) {
      case "welcome":
        return <WelcomeSection onNext={handleNext} />;
      case "basic":
        return <BasicInfoSection data={basicInfo} onChange={(key, value) => setBasicInfo({...basicInfo, [key]: value})} errors={errors} />;
      case "category":
        return <CategorySection data={category} onChange={(key, value) => setCategory({...category, [key]: value})} errors={errors} />;
      case "impact":
        return <ImpactSection data={impact} onChange={(key, value) => setImpact({...impact, [key]: value})} errors={errors} />;
      case "values":
        return <ValuesSection data={values} onChange={(key, value) => setValues({...values, [key]: value})} errors={errors} />;
      case "digital":
        return <DigitalSection data={digital} onChange={(key, value) => setDigital({...digital, [key]: value})} headshot={headshot} onHeadshotChange={setHeadshot} errors={errors} />;
      case "reference":
        return <ReferenceSection data={reference} onChange={(key, value) => setReference({...reference, [key]: value})} />;
      case "terms":
        return <TermsSection data={{ agreed_to_terms: agreedToTerms }} onChange={(key, value) => setAgreedToTerms(value)} errors={errors} />;
      default:
        return null;
    }
  };

  // Success screen after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#f4f7fb] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
              alt="JamBassador Awards" 
              className="h-20 w-auto mx-auto"
            />
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center animate-fade-in">
            <div className="w-20 h-20 bg-[#2bb673]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#2bb673]" />
            </div>
            <h1 className="font-serif text-3xl text-[#132238] mb-4">
              Thank You for Applying!
            </h1>
            <p className="font-sans text-[#4b5563] mb-6">
              Your application to <strong>JamBassadors 2026</strong> has been successfully submitted.
              Our selection committee will review your application and selected candidates will be contacted directly.
            </p>
            <div className="bg-[#e9f8f0] border border-[#2bb673]/30 rounded-xl p-4 mb-6">
              <p className="font-sans text-sm text-[#16643d]">
                <strong>What's Next?</strong> If selected, you will receive an official invitation with full details about participation and the Heroes Awards Luncheon on Saturday, October 17th, 2026.
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0b3d91] text-white font-sans font-semibold rounded-full hover:bg-[#0a3580] transition-colors"
              data-testid="btn-return-home"
            >
              Return Home <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="form-logo-link">
            <img 
              src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
              alt="JamBassador Awards" 
              className="h-16 md:h-20 w-auto"
            />
          </a>
          {currentStep > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-sans text-sm text-[#4b5563]">Step {currentStep} of {steps.length - 1}</span>
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#0b3d91] transition-all duration-300"
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {errors.submit}
          </div>
        )}
        
        {renderCurrentStep()}

        {/* Navigation Buttons */}
        {currentStep > 0 && (
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrev}
              className="flex items-center gap-2 px-6 py-3 font-sans font-medium text-[#4b5563] hover:text-[#132238] transition-colors"
              data-testid="btn-prev"
            >
              <ArrowLeft size={18} /> Back
            </button>
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 bg-[#0b3d91] text-white font-sans font-semibold rounded-full hover:bg-[#0a3580] transition-colors"
                data-testid="btn-next"
              >
                Continue <ArrowRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-8 py-3 bg-[#f2c94c] text-[#132238] font-sans font-bold rounded-full hover:bg-[#e5bc3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    Submit Application <ArrowRight size={18} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ApplicationForm;
